import React from 'react';
import { Mail, User, ShieldCheck, Timer, FileSpreadsheet, MonitorSmartphone, ArrowRight, Lock } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';

const RULES = [
  { icon: Timer, text: '20 minutes, enforced by the server. The test auto-submits when time is up.' },
  { icon: FileSpreadsheet, text: 'Hands-on tasks in a live spreadsheet + short scenario questions, based on the skills you choose.' },
  { icon: ShieldCheck, text: 'Leaving the window and pasting from outside the test are recorded and blocked.' },
  { icon: MonitorSmartphone, text: 'Use a laptop or desktop with a stable connection for the best experience.' },
];

export const RegisterStep = ({ test, invite, form, setForm, onNext }) => {
  const valid = form.name.trim().length >= 2 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim()) && form.consent;
  return (
    <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 items-start" data-testid="assessment-register-step">
      <div className="space-y-6 pt-2">
        <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full">
          <FileSpreadsheet className="h-3.5 w-3.5" /> Talent Assessment
        </span>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.05]" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {test.title}
        </h1>
        <p className="text-slate-600 text-base leading-relaxed max-w-xl">{test.description}</p>
        <ul className="space-y-3">
          {RULES.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-start gap-3 text-sm text-slate-700">
              <span className="mt-0.5 w-7 h-7 rounded-md bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0">
                <Icon className="h-3.5 w-3.5 text-indigo-600" />
              </span>
              {text}
            </li>
          ))}
        </ul>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl shadow-[0_20px_60px_-30px_rgba(79,70,229,0.35)] p-7 space-y-5">
        <div>
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {invite ? `Welcome, ${invite.name.split(' ')[0]}` : 'Tell us who you are'}
          </h2>
          <p className="text-sm text-slate-500 mt-1">Your details are only shared with the hiring team.</p>
        </div>
        <label className="block space-y-1.5">
          <span className="text-xs font-semibold text-slate-600">Full name</span>
          <div className="relative">
            <User className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input className="pl-9 h-11 bg-white text-slate-900" value={form.name} placeholder="e.g. Priya Sharma"
              onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="candidate-name-input" />
          </div>
        </label>
        <label className="block space-y-1.5">
          <span className="text-xs font-semibold text-slate-600">Email</span>
          <div className="relative">
            <Mail className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input className={`pl-9 h-11 text-slate-900 ${invite?.email ? 'bg-slate-50 pr-9 font-medium' : 'bg-white'}`} type="email" value={form.email} placeholder="you@example.com"
              readOnly={!!invite?.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="candidate-email-input" />
            {invite?.email && <Lock className="h-4 w-4 text-slate-500 absolute right-3 top-1/2 -translate-y-1/2" data-testid="candidate-email-locked-icon" />}
          </div>
          {invite?.email && <span className="block text-[11px] text-slate-500">This personal link is linked to your email address.</span>}
        </label>
        <label className="flex items-start gap-2.5 text-xs text-slate-600 cursor-pointer">
          <Checkbox checked={form.consent} onCheckedChange={(v) => setForm({ ...form, consent: !!v })} data-testid="candidate-consent-checkbox" className="mt-0.5" />
          I understand the test is timed and that window switches and outside pastes are monitored.
        </label>
        <Button className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white gap-2 transition-colors" disabled={!valid}
          onClick={onNext} data-testid="continue-to-skills-btn">
          Choose my skills <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default RegisterStep;
